import { Button } from "@/components/ui/baseUi/button"
import { withForm } from "@/components/ui/forms/hooks/useFormHook"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { BoxProgramInfoForm } from "@/modules/boxProgrammation/components/boxProgramInfoForm"
import type { BoxProgramDetailsEntity } from "@/modules/boxProgrammation/entities/boxProgramEntity"
import type { BoxProgramFormFields } from "@/modules/boxProgrammation/formik/boxProgramFormTypes"
import { useDeleteBoxProgram } from "@/modules/boxProgrammation/hooks/useBoxProgram"
import { cn } from "@/modules/lib/utils"
import { AlertIconDark } from "@images/svgIcons/alertIcon"
import { word } from "@modules/core/utils/i18n"
import { useBoxNavigate } from "@modules/navigation/hooks/useBoxNavigate"
import { Path } from "@modules/navigation/routes"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"

export const BoxProgrammationForm = withForm({
  defaultValues: {} as BoxProgramFormFields,
  props: {} as {
    boxProgrammation?: BoxProgramDetailsEntity
    onValidate: () => void
    className?: string
  } & React.ComponentProps<"form">,
  render: function BoxProgrammationFormInner({ form, boxProgrammation, onValidate, className, ...props }) {
    const confirmPopup = useConfirmPopup()
    const { mutateAsync: deleteBoxProgram } = useDeleteBoxProgram()
    const boxNavigate = useBoxNavigate()
    const { hideSidebar } = useSidebar()

    const confirmationModaleContent = (
      <div className="flex w-full flex-col items-center gap-2">
        <div className="mb-6 flex w-full flex-col items-center">
          <AlertIconDark color="currentColor" className="text-textError" />
          <Text fontStyle="TL">{word("programmation.form.delete.confirmation.title")}</Text>
        </div>
        {boxProgrammation?.restrictions && boxProgrammation.restrictions.length > 0 ? (
          <div className="flex flex-col items-center">
            <Text fontStyle="BM" className="text-center" style={{ whiteSpace: "pre-line" }}>
              {word("programmation.form.delete.confirmation.content.with_restrictions_1", {
                programmationName: boxProgrammation.name,
              })}
            </Text>
            <ul>
              {boxProgrammation.restrictions.map((restriction) => (
                <li key={restriction.id}>
                  <Text fontStyle="BM" className="mb-4 text-center break-all" style={{ whiteSpace: "pre-line" }}>
                    {restriction.name}
                  </Text>
                </li>
              ))}
            </ul>

            <Text fontStyle="BM" className="mt-6 mb-4 text-center" style={{ whiteSpace: "pre-line" }}>
              {word("programmation.form.delete.confirmation.content.with_restrictions_2")}
            </Text>
          </div>
        ) : (
          <Text fontStyle="BM" className="mb-4 text-center" style={{ whiteSpace: "pre-line" }}>
            {word("programmation.form.delete.confirmation.content.no_restrictions", {
              programmationName: boxProgrammation?.name,
            })}
          </Text>
        )}
      </div>
    )

    const confirmDeleteProgram = () => {
      if (!boxProgrammation) return

      confirmPopup.show({
        title: "",
        message: confirmationModaleContent,
        classNames: {
          dialogContent: "w-128",
        },
        variant: {
          dialogFooterConfirm: "negativePrimary",
        },
        accept: async () => {
          try {
            await deleteBoxProgram(boxProgrammation.id)

            toast.success(word("programmation.delete.confirm.message"), {
              classNames: successToastClassName(),
            })

            setTimeout(() => boxNavigate(Path.Box.Planning), 500)
          } catch (e: unknown) {
            const error = e as { code?: string; message: string }
            toast.error(
              error.code === "OFFER_LINKED_TO_PROGRAM"
                ? word("programmation.delete.error.linked_to_offer")
                : error.message,
              {
                classNames: errorToastClassName(),
              },
            )
          }
        },
        footerProps: {
          cancelText: word("global.cancel"),
          confirmText: word("global.delete"),
        },
      })
    }

    return (
      <form
        className={cn("flex w-full flex-col justify-center px-5 pt-18 pb-6", className)}
        onSubmit={(e) => {
          e.preventDefault()
          onValidate()
        }}
        {...props}
      >
        <Text fontStyle="HS" className="mb-4">
          {boxProgrammation ? word("box.programmation.form.update.title") : word("box.programmation.form.add.title")}
        </Text>

        <Text fontStyle="BS" className="mb-4">
          {word("box.programmation.form.add.description")}
        </Text>

        <div className="flex flex-col">
          <div className="flex flex-col justify-center">
            <BoxProgramInfoForm isEditing={!!boxProgrammation} form={form} />

            <div className="mt-8 mb-12 flex w-full justify-end">
              <div className="flex grow flex-col gap-4 sm:flex-row">
                {boxProgrammation ? (
                  <Button variant="negativePrimary" size="lg" onClick={() => confirmDeleteProgram()} className="grow">
                    {word("global.delete")}
                  </Button>
                ) : (
                  <Button variant="outline" size="lg" onClick={() => hideSidebar()} className="grow">
                    {word("global.cancel")}
                  </Button>
                )}

                <Button variant="primary" size="lg" className="grow" type="submit">
                  {word("global.next")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </form>
    )
  },
})
